import React from 'react';
import { useLocation } from 'react-router-dom';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import circleDismissSVG from '@plone/volto/icons/circle-dismiss.svg';
import {
  clearCart,
  formatCartPrice,
  getCartTotal,
  getCartTypeFromPathname,
  removeCartItem,
  updateCartItemQuantity,
  useCart,
} from '../../../cart/cart';

type CartBlockProps = {
  data?: {
    title?: string;
    description?: string;
  };
  id?: string;
};

const CartBlock = ({ data, id = 'cart' }: CartBlockProps) => {
  const { pathname } = useLocation();
  const cartType = getCartTypeFromPathname(pathname);
  const cart = useCart(cartType);
  const title = data?.title || 'Warenkorb';

  return (
    <div
      aria-labelledby={`cart-block-title-${id}`}
      className="block cart-block"
    >
      <div className="q container default">
        <h2 id={`cart-block-title-${id}`}>{title}</h2>
        {data?.description && <p>{data.description}</p>}
        {!cartType ? (
          <p>
            Dieser Warenkorb-Block muss auf <code>/seminare/cart</code> oder{' '}
            <code>/muenchenprogramm/cart</code> verwendet werden.
          </p>
        ) : cart.items.length === 0 ? (
          <p>Der Warenkorb ist leer.</p>
        ) : (
          <>
            <div className="cart-block-table-wrapper">
              <table className="cart-block-table">
                <thead>
                  <tr>
                    <th scope="col">Titel</th>
                    <th scope="col">Preis</th>
                    <th scope="col">Anzahl</th>
                    <th scope="col">Gesamt</th>
                    <th scope="col">
                      <span className="visually-hidden">Aktion</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {cart.items.map((item) => (
                    <tr key={item.seminarId}>
                      <td>{item.topic || `Seminar ${item.seminarId}`}</td>
                      <td>{formatCartPrice(item.price)}</td>
                      <td>
                        <select
                          aria-label={`Anzahl für ${item.topic || 'Kurs'}`}
                          className="cart-block-quantity"
                          value={item.quantity}
                          onChange={(event) =>
                            updateCartItemQuantity(
                              cartType,
                              item.seminarId,
                              Number(event.target.value),
                            )
                          }
                        >
                          {[1, 2, 3, 4].map((quantity) => (
                            <option key={quantity} value={quantity}>
                              {quantity}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        {formatCartPrice(
                          typeof item.price === 'number'
                            ? item.price * item.quantity
                            : undefined,
                        )}
                      </td>
                      <td>
                        <button
                          aria-label={`${item.topic || 'Kurs'} entfernen`}
                          className="cart-block-remove"
                          type="button"
                          onClick={() =>
                            removeCartItem(cartType, item.seminarId)
                          }
                        >
                          <Icon name={circleDismissSVG} size="24px" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <th colSpan={3} scope="row">
                      Warenkorbpreis
                    </th>
                    <td>{formatCartPrice(getCartTotal(cart))}</td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
            <div className="cart-block-actions">
              <button
                className="cart-block-clear"
                type="button"
                onClick={() => clearCart(cartType)}
              >
                <span>Liste leeren</span>
                <span aria-hidden="true" className="cart-block-clear-icon">
                  ×
                </span>
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CartBlock;
